import storeManager from '../StoreManager';
import questionReader from './QuestionReader';
import sessionReader from './SessionReader';
import settingsReader from './SettingsReader';
import tocReader from './TocReader';
import { reportError } from '../../services/ErrorReporting';
import objectTypes from '../../constants/ObjectTypes';
import constants from '../../qti/Constants';

const assessmentReader = {
    /**
     * Checks if the assessment is loaded.
     * Returns true for dummy assessment.
     *
     * @param {Object} state The full state
     * @returns {boolean}
     */
    isAssessmentLoaded(state = storeManager.getState()) {
        return !!(state.assessment && Array.isArray(state.assessment.sections));
    },

    /**
     * Returns true only when real assessment fetched and parsed.
     * Returns false for dummy assessment.
     * @returns {boolean}
     */
    isAssessmentReady() {
        const { assessment } = storeManager.getState();
        return assessment?.contentRevisionId > 0;
    },

    getAssessment(state = storeManager.getState()) {
        return state.assessment;
    },

    getQuestionWithIndexAndSectionId(contentRevisionId, state = storeManager.getState()) {
        const { sections = [] } = state.assessment;
        let sectionId;
        let sequenceIndex = 0;
        let question;

        function finder(currentQuestion) {
            sequenceIndex++;
            return (currentQuestion.contentRevisionId === contentRevisionId);
        }

        // Loop through the sections until the question is found.
        const isFound = sections.some((section) => {
            const { questions = [] } = section;
            const foundQuestion = questions.find(finder);

            if (foundQuestion) {
                question = foundQuestion;
                sectionId = section.id;

                return true;
            }

            return false;
        });

        return isFound ? { sectionId, sequenceIndex, question } : null;
    },

    /**
     * Returns a question from the state
     * @param contentRevisionId
     * @param state Complete state
     * @returns {contentRevisionId, itemBody, responseDeclarations, componentBody, responseModels}
     */
    getQuestion(contentRevisionId, state = storeManager.getState()) {
        try {
            const { assessment } = state;

            const stimulusLocationData = tocReader.getStimulusLocation(contentRevisionId);
            if (stimulusLocationData) {
                const sectionWithStimulus = assessment.sections[stimulusLocationData.sectionIndex];
                return sectionWithStimulus ? sectionWithStimulus.stimulusContent : undefined;
            }

            const locationData = tocReader.getQuestionLocation(contentRevisionId);
            if (locationData && assessment.sections) {
                return assessment.sections[locationData.sectionIndex]?.questions[locationData.questionIndex];
            }

            const data = this.getQuestionWithIndexAndSectionId(contentRevisionId, state);
            return data ? data.question : undefined;
        } catch (error) {
            reportError(error);
        }
        return undefined;
    },

    /**
     * Gets an array of all questions from all sections, sorted in order.
     *
     * @param {Object} assessment The assessment state
     * @returns {Array}
     */
    getQuestionsIndex(assessment) {
        const { sections } = assessment;
        const itemsOrderAllView = [];
        const itemsOrder = [];
        const itemsMap = {};
        const sectionStimulusMap = {};
        let overallIndex = -1;
        let overallProperQuestionsIndex = -1;
        let overallRespondableItemsIndex = -1;
        let numberOfContentBlocks = 0;
        let numberOfDocuments = 0;
        let numberOfForms = 0;
        let numberOfQuestions = 0;

        if (!sections) {
            return {
                itemsMap,
                itemsOrder,
                itemsOrderAllView,
                numberOfContentBlocks,
                numberOfDocuments,
                numberOfForms,
                numberOfQuestions,
                sectionStimulusMap,
            };
        }

        sections.forEach((section, sectionIndex) => {
            if (section.stimulusContent) {
                overallRespondableItemsIndex++;
                const item = {
                    contentRevisionId: section.stimulusContent.contentRevisionId,
                    isFirstQuestion: false,
                    isLastQuestion: false,
                    overallIndex: -1,
                    overallRespondableItemsIndex,
                    questionIndex: -1,
                    sectionId: section.id,
                    sectionIndex,
                    type: objectTypes.CATEGORY.STIMULUS,
                };

                sectionStimulusMap[section.stimulusContent.contentRevisionId] = item;
                itemsOrderAllView.push(item);
            }

            section.questions.forEach((question, questionIndex) => {
                overallIndex++;
                const isActualQuestion = !questionReader.isDocument(question) && !questionReader.isForm(question);
                if (isActualQuestion) {
                    overallProperQuestionsIndex++;
                }
                overallRespondableItemsIndex++;

                const isFirstQuestion = sectionIndex === 0 && questionIndex === 0;
                const isLastQuestion = sectionIndex === (sections.length - 1) &&
                    questionIndex === (section.questions.length - 1);

                let type = 'question';
                if (questionReader.isDocument(question)) {
                    type = objectTypes.CATEGORY.DOCUMENT;
                } else if (questionReader.isForm(question)) {
                    type = objectTypes.CATEGORY.FORM;
                } else if (questionReader.isStimulus(question) && questionReader.isGapMatchQuestion(question)) {
                    type = objectTypes.CATEGORY.MATCH_IN_STIMULUS;
                }

                const item = {
                    contentRevisionId: question.contentRevisionId,
                    isFirstQuestion,
                    isLastQuestion,
                    overallIndex,
                    questionIndex,
                    sectionId: sections[sectionIndex].id,
                    sectionIndex,
                    type,
                };

                if (isActualQuestion) {
                    item.overallProperQuestionsIndex = overallProperQuestionsIndex;
                }
                item.overallRespondableItemsIndex = overallRespondableItemsIndex;
                itemsOrder.push(item);
                itemsOrderAllView.push(item);

                itemsMap[question.contentRevisionId] = overallIndex;

                if (questionReader.isDocument(question)) {
                    numberOfDocuments++;
                } else if (questionReader.isForm(question)) {
                    numberOfForms++;
                } else if (questionReader.isContentBlock(question)) {
                    numberOfContentBlocks++;
                } else {
                    numberOfQuestions++;
                }
            });
        });

        return {
            itemsMap,
            itemsOrder,
            itemsOrderAllView,
            numberOfContentBlocks,
            numberOfDocuments,
            numberOfForms,
            numberOfQuestions,
            sectionStimulusMap,
        };
    },

    getSectionById(sectionId, { assessment } = storeManager.getState()) {
        return assessment.sections?.find(section => (section.id === sectionId));
    },

    getSectionByAdditionalResource(resourceItemId) {
        const { assessment: { sections = [] } } = storeManager.getState();
        return sections.find(({ additionalResources = [] }) => (
            !!additionalResources
                .filter(({ type }) => type === 'audio')
                .find(({ contentItemId }) => contentItemId === resourceItemId)
        ));
    },

    getNestedSectionById(sectionId) {
        const { assessment: { sections = [] } } = storeManager.getState();
        for (const { sections: nestedSections = [] } of sections) {
            const found = nestedSections.find(nestedSection => nestedSection.id === sectionId);
            if (found) {
                return found;
            }
        }
        return undefined;
    },

    getCurrentSection() {
        const currentSectionId = storeManager.getState().currentlyShown.sectionId;
        return this.getSectionById(currentSectionId);
    },

    getCurrentQuestion() {
        const currentQuestionId = storeManager.getState().currentlyShown.questionId;
        return this.getQuestion(currentQuestionId);
    },

    isContentLanguageSupported(contentLanguage, state = storeManager.getState()) {
        return state.assessment.supportedLanguages && state.assessment.supportedLanguages.includes(contentLanguage);
    },

    getSupportedLanguages(state = storeManager.getState()) {
        return state.assessment.supportedLanguages || [];
    },

    getQuestionSketchCode(questionRevisionId, state = storeManager.getState()) {
        if (state.assessment && state.assessment.sketchCodes && state.assessment.sketchCodes[questionRevisionId]) {
            return state.assessment.sketchCodes[questionRevisionId].sketchCode;
        } else if (sessionReader.isContextExam()) {
            return undefined;
        }

        return 'XXXXXXX';
    },

    getFeedbackForQuestion(questionId, state = storeManager.getState()) {
        return state.assessment.feedbacks ? state.assessment.feedbacks[`${questionId}`] : undefined;
    },

    /**
     * Returns true if section layout value is vertical-left or vertical-top.
     *
     * @param {Number} sectionId The assessment state
     * @param {Object} state The assessment state
     * @returns {Boolean}
     */
    useVerticalDisplayForSection(sectionId, state = storeManager.getState()) {
        if (state.assessment) {
            const currentSection = this.getSectionById(sectionId);
            if (currentSection && currentSection.layout) {
                return currentSection.layout.indexOf('vertical') > -1;
            }
        }
        // TODO: Remove check in settings when vertical_display key has been removed from GetSettings
        const settings = settingsReader.getSettingsForSection(sectionId);
        return !!settings.vertical_display;
    },

    getSectionQuestions(sectionId) {
        const section = this.getSectionById(sectionId);
        return section ? section.questions : null;
    },

    getSectionQuestionsCount(sectionId, state = storeManager.getState()) {
        const currentSection = this.getSectionById(sectionId, state);
        if (currentSection) {
            return currentSection.questions.length;
        }

        return -1;
    },

    getSectionQuestionIds(sectionId, state = storeManager.getState()) {
        const section = this.getSectionById(sectionId, state);
        return section ? section.questions.map(question => question.contentRevisionId) : [];
    },

    getSections() {
        const state = storeManager.getState();
        return state.assessment.sections || [];
    },

    isRichCorrectResponses() {
        const { objectType } = constants;
        const allowedObjectTypes = [objectType.NUMERIC_SIMULATION, objectType.SYMBOLIC_MATH];

        const sections = storeManager.getState()?.assessment?.sections || [];

        return sections.some(section =>
            section.questions.some(question => allowedObjectTypes.includes(question.objectType))
        );
    },

    getSectionStimulusPosition(section) {
        return (section?.layout && section?.stimulusContent) ? section.layout.split('-')[1] : null;
    },

    getStimulusForSectionId(sectionId, state = storeManager.getState()) {
        const section = this.getSectionById(sectionId, state);
        return section ? section.stimulusContent : undefined;
    },

    getCustomFieldsForSectionId(sectionId, state = storeManager.getState()) {
        const section = this.getSectionById(sectionId, state);
        return section ? section.customFields : undefined;
    },

    hasBackgroundAudioResource(state = storeManager.getState()) {
        return !!this.getBackgroundAudioResource(state);
    },

    getBackgroundAudioResource(state = storeManager.getState()) {
        const { assessment } = state;
        const { additionalResources = [] } = sessionReader.isModeSection() ? assessment.sections[0] : assessment;
        return additionalResources.find((resource) => { return resource.type === constants.additionalResourceType.AUDIO; });
    },

    getAllStimulusIds(state = storeManager.getState()) {
        const { sections = [] } = state.assessment;

        return sections
            .map(item => (this.getStimulusForSectionId(item.id)?.contentRevisionId))
            .filter(item => !!item);
    },

    getStimulusSectionNumber(stimulusId, state = storeManager.getState()) {
        const { sections = [] } = state.assessment;

        for (let i = 0; i < sections.length; i++) {
            const stimulus = this.getStimulusForSectionId(sections[i].id);
            if (stimulus && stimulus.contentRevisionId === stimulusId) {
                return stimulus.sectionNumber || '';
            }
        }

        return '';
    },

    hasMatchQuestions(section) {
        if (!section) {
            return false;
        }

        const { interactionType } = constants;
        const { questions = [], stimulusContent = {} } = section;
        const items = [...questions, ...[stimulusContent]];
        const gapMatchTypes = [interactionType.GAP_MATCH_INTERACTION, interactionType.DRAG_AND_DROP_INTERACTION];

        return items.some(({ responseModels }) => responseModels?.some(({ type }) => gapMatchTypes.includes(type)));
    },

    getCurrentShownSectionIndex() {
        const state = storeManager.getState();
        const { sections } = state.assessment;
        const currentSectionId = state.currentlyShown.sectionId;
        if (!currentSectionId) {
            return false;
        }
        return sections.findIndex(section => section.id === currentSectionId);
    },

    getCurrentShownSection() {
        const {
            assessment: { sections = [] },
            currentlyShown: { sectionId },
        } = storeManager.getState();

        const currentSectionId = parseInt(sectionId, 10);
        return sections.find(section => section.id === currentSectionId);
    },

    getCorrectResponse({ contentRevisionId, responseIdentifier }) {
        const { correctResponses } = storeManager.getState();
        return correctResponses?.[contentRevisionId]?.[responseIdentifier] || null;
    },

    isCommittingAnswerInPreviewSupported() {
        const { assessment } = storeManager.getState();
        return !!assessment.supportsCommittingAnswer;
    },

    isBackwardNavigationInPreviewDisallowed() {
        const { assessment } = storeManager.getState();
        return !!assessment.shouldNavigateStrictlySequentially;
    },
};

export default assessmentReader;
